/**
 * Document Management Module - Compliance Dashboard
 * 
 * Comprehensive dashboard showing:
 * - Overall compliance status
 * - Framework breakdown
 * - Compliance gaps
 * - Document statistics
 * - Recent notifications
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaFileAlt,
  FaClock,
  FaBan,
  FaBell,
  FaChartBar,
  FaSearch,
} from 'react-icons/fa';
import { ComplianceDashboard as DashboardData, ComplianceGap, Notification, ComplianceStatus } from './types';
import documentService from './documentService';
import './DocumentManagement.css';

const ComplianceDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [gaps, setGaps] = useState<ComplianceGap[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardData, gapsData, notificationsData] = await Promise.all([
        documentService.getComplianceDashboard(),
        documentService.getComplianceGaps(),
        documentService.getNotifications(true), // Unread only
      ]);
      setDashboard(dashboardData);
      setGaps(gapsData);
      setNotifications(notificationsData);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Compliant':
        return 'success';
      case 'Partially Compliant':
        return 'warning';
      case 'Non-Compliant':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Compliant':
        return <FaCheckCircle className="text-success" size={24} />;
      case 'Partially Compliant':
        return <FaExclamationTriangle className="text-warning" size={24} />;
      case 'Non-Compliant':
        return <FaTimesCircle className="text-danger" size={24} />;
      default:
        return null;
    }
  };

  const getGapSeverityClass = (severity: string) => {
    return `gap-severity-badge ${severity.toLowerCase()}`;
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="document-management-container">
        <div className="container-fluid p-4" style={{ paddingTop: '2rem', zoom: 0.9 }}>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return null;
  }

  return (
    <div className="document-management-container">
      <div className="container-fluid p-4" style={{ paddingTop: '2rem', zoom: 0.9 }}>
        {/* Header */}
        <div className="mb-4">
          <h2>Compliance Dashboard</h2>
          <p className="text-muted">
            Overview of compliance status, gaps, and document management metrics
          </p>
        </div>

        {/* Overall Status Card */}
        <div className={`card mb-4 dashboard-stat-card ${dashboard.overallStatus.toLowerCase().replace(' ', '-')}`}>
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-8">
                <div className="d-flex align-items-center gap-3 mb-2">
                  {getStatusIcon(dashboard.overallStatus)}
                  <div>
                    <h3 className="mb-0">Overall Compliance Status</h3>
                    <p className="text-muted mb-0">
                      {dashboard.compliantControls} of {dashboard.totalControls} controls are
                      compliant
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4 text-end">
                <div
                  className={`compliance-status-badge ${dashboard.overallStatus.toLowerCase().replace(' ', '-')}`}
                  style={{ fontSize: '1.2rem', padding: '8px 20px' }}
                >
                  {dashboard.overallStatus}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row g-4 mb-4">
          {/* Controls Status */}
          <div className="col-md-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="text-muted mb-0">Controls</h6>
                  <FaChartBar className="text-primary" />
                </div>
                <h3 className="mb-1">{dashboard.totalControls}</h3>
                <div className="small">
                  <span className="text-success">
                    {dashboard.compliantControls} Compliant
                  </span>
                  {' | '}
                  <span className="text-warning">
                    {dashboard.partiallyCompliantControls} Partial
                  </span>
                  {' | '}
                  <span className="text-danger">
                    {dashboard.nonCompliantControls} Non-Compliant
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Documents Status */}
          <div className="col-md-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="text-muted mb-0">Documents</h6>
                  <FaFileAlt className="text-info" />
                </div>
                <h3 className="mb-1">{dashboard.totalDocuments}</h3>
                <div className="small">
                  <span className="text-success">
                    {dashboard.approvedDocuments} Approved
                  </span>
                  {' | '}
                  <span className="text-warning">
                    {dashboard.pendingApproval} Pending
                  </span>
                  {' | '}
                  <span className="text-danger">
                    {dashboard.expiredDocuments} Expired
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Gaps */}
          <div className="col-md-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="text-muted mb-0">Open Gaps</h6>
                  <FaExclamationTriangle className="text-warning" />
                </div>
                <h3 className="mb-1">{dashboard.openGaps}</h3>
                <div className="small">
                  <span className="text-danger">
                    {dashboard.criticalGaps} Critical
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Expiring Documents */}
          <div className="col-md-3">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="text-muted mb-0">Expiring Soon</h6>
                  <FaClock className="text-warning" />
                </div>
                <h3 className="mb-1">{dashboard.expiringSoon}</h3>
                <div className="small text-muted">
                  Documents expiring in next 30 days
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Framework Breakdown */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Framework Compliance Status</h5>
              </div>
              <div className="card-body">
                {dashboard.frameworkBreakdown.map((framework: { framework: string; totalControls: number; compliant: number; status: ComplianceStatus }, idx: number) => (
                  <div key={idx} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h6 className="mb-0">{framework.framework}</h6>
                        <small className="text-muted">
                          {framework.compliant} of {framework.totalControls} controls compliant
                        </small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {getStatusIcon(framework.status)}
                        <span
                          className={`compliance-status-badge ${framework.status.toLowerCase().replace(' ', '-')}`}
                        >
                          {framework.status}
                        </span>
                      </div>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div
                        className={`progress-bar bg-${getStatusColor(framework.status)}`}
                        role="progressbar"
                        style={{
                          width: `${(framework.compliant / framework.totalControls) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Notifications */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaBell className="me-2" />
                  Recent Notifications
                </h5>
                <button
                  className="btn btn-sm btn-link"
                  onClick={() => navigate('/documents/notifications')}
                >
                  View All
                </button>
              </div>
              <div className="card-body">
                {notifications.length === 0 ? (
                  <div className="text-center text-muted p-4">
                    <FaBell size={48} className="mb-3 opacity-50" />
                    <p>No new notifications</p>
                  </div>
                ) : (
                  notifications.slice(0, 5).map(notification => (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.severity?.toLowerCase() || 'medium'} mb-3 p-3 rounded`}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{notification.title}</h6>
                          <p className="mb-1 small">{notification.message}</p>
                          <small className="text-muted">
                            {formatDate(notification.createdAt)}
                          </small>
                        </div>
                        {(notification.relatedDocumentId || notification.documentId) && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/documents/${notification.relatedDocumentId || notification.documentId}`)}
                          >
                            <FaSearch />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Gaps */}
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaExclamationTriangle className="me-2" />
              Compliance Gaps
            </h5>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => navigate('/documents/gaps')}
            >
              View All Gaps
            </button>
          </div>
          <div className="card-body">
            {gaps.length === 0 ? (
              <div className="text-center text-muted p-4">
                <FaCheckCircle size={48} className="mb-3 text-success opacity-50" />
                <p>No compliance gaps detected</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Control</th>
                      <th>Framework</th>
                      <th>Gap Type</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th>Detected</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gaps.slice(0, 10).map(gap => (
                      <tr key={gap.id}>
                        <td>
                          <div>
                            <strong>{gap.controlName}</strong>
                            <div className="small text-muted">{gap.controlId}</div>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-primary">{gap.framework}</span>
                        </td>
                        <td>{gap.gapType || 'N/A'}</td>
                        <td>
                          <span className={getGapSeverityClass(gap.severity)}>
                            {gap.severity}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              gap.status === 'Open'
                                ? 'bg-danger'
                                : gap.status === 'In Progress'
                                ? 'bg-warning'
                                : 'bg-success'
                            }`}
                          >
                            {gap.status}
                          </span>
                        </td>
                        <td className="small text-muted">{formatDate(gap.detectedAt)}</td>
                        <td>
                          {gap.relatedDocumentId && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => navigate(`/documents/${gap.relatedDocumentId!}`)}
                            >
                              View Document
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboard;

